import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SEO from "../../components/SEO";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  description: string;
  photo_main_url: string;
  photo_hover_url: string;
}

const TeamShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [teamMember, setTeamMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (id) {
      fetch(`/api/teams/${id}?lang=${i18n.language}`)
        .then((res) => res.json())
        .then((data) => {
          setTeamMember(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Team member fetch error:", err);
          setLoading(false);
        });
    }
  }, [id, i18n.language]);

  if (loading) {
    return null;
  }

  if (!teamMember) {
    navigate('/team', { replace: true });
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 10, pb: 6 }}>
      <SEO
        title={`${teamMember.name} - ${t("pageTitles.team")}`}
        description={`${teamMember.name} - ${teamMember.role}. ${teamMember.description}`}
        keywords={`${teamMember.name}, ${teamMember.role}, tiyatro ekibi, sanat eğitmeni`}
      />

      <Grid container spacing={{ xs: 3, md: 6 }} sx={{ minHeight: { xs: 'auto', md: '600px' } }}>
        {/* Left Side */}
        <Grid size={{ xs: 12, sm: 6, md: 5 }}>
          <Box sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Paper
              elevation={8}
              sx={{
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                height: { xs: 'auto', sm: 'auto', md: '100%' },
                mb: { xs: 3, md: 0 }
              }}
            >
              <Box
                component="img"
                src={teamMember.photo_main_url}
                alt={teamMember.name}
                sx={{
                  width: "100%",
                  aspectRatio: "2/3",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Paper>
          </Box>
        </Grid>

        {/* Right Side */}
        <Grid size={{ xs: 12, sm: 6, md: 7 }}>
          <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'column', md: 'row' },
            alignItems: { xs: 'center', sm: 'center', md: 'center' },
            justifyContent: { xs: 'center', sm: 'center', md: 'center' },
            gap: { xs: 2, sm: 3, md: 4 },
            textAlign: { xs: 'center', sm: 'center', md: 'center' }
          }}>
            {/* Name */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              height: { xs: 'auto', sm: 'auto', md: '100%' },
              mb: { xs: 2, sm: 2, md: 0 }
            }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "var(--color-brand-red-text)",
                  writingMode: { xs: 'horizontal-tb', sm: 'horizontal-tb', md: 'vertical-rl' },
                  textOrientation: { xs: 'mixed', sm: 'mixed', md: 'mixed' },
                  transform: { xs: 'none', sm: 'none', md: 'rotate(180deg)' },
                  textAlign: "center",
                  lineHeight: 1,
                  letterSpacing: "0.1em",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                  fontSize: { xs: '2.5rem', sm: '2rem', md: '3rem' }
                }}
              >
                {teamMember.name}
              </Typography>
            </Box>

            {/* Cv */}
            <Box sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 2, sm: 2, md: 3 },
              width: '100%',
              justifyContent: { xs: 'center', sm: 'center', md: 'center' },
              alignItems: { xs: 'center', sm: 'center', md: 'center' }
            }}>
              {/* Role */}
              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: "600",
                  color: "#fff",
                  textDecoration: "underline",
                  textUnderlineOffset: "8px",
                  textDecorationColor: "var(--color-brand-red-text)",
                  fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
                  textAlign: { xs: 'center', sm: 'center', md: 'center' }
                }}
              >
                {teamMember.role}
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                  color: "#fff",
                  textAlign: { xs: 'center', sm: 'center', md: 'center' },
                  px: { xs: 2, sm: 2, md: 0 },
                  maxWidth: { xs: '100%', sm: '100%', md: '500px' }
                }}
              >
                {teamMember.description}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default TeamShow;